import { useState } from 'react';
import type { Supplier } from '@salonomy/types';
import { Button, Field, Modal, TextInput, Toggle } from '@salonomy/ui';
import { useCreateSupplier, useUpdateSupplier } from '../../data/stock.js';
import { useToast } from '../../lib/toast.js';
import './stock.css';

/**
 * Create / edit a supplier. Plain attribute CRUD — name, contact, account ref, notes. On edit an
 * `active` toggle soft-deactivates (no erase); a deactivated supplier stays inspectable in the list.
 */
export function SupplierModal({ existing, onClose }: { existing?: Supplier; onClose: () => void }) {
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const toast = useToast();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [email, setEmail] = useState(existing?.contactEmail ?? '');
  const [phone, setPhone] = useState(existing?.contactPhone ?? '');
  const [accountRef, setAccountRef] = useState(existing?.accountRef ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [active, setActive] = useState(existing?.active ?? true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) {
      toast.error('A supplier name is required.');
      return;
    }
    setBusy(true);
    const attrs = {
      name: name.trim(),
      contactEmail: email.trim() || null,
      contactPhone: phone.trim() || null,
      accountRef: accountRef.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      if (editing && existing) {
        await update.mutateAsync({ id: existing.id, body: { ...attrs, active } });
        toast.success('Supplier updated');
      } else {
        await create.mutateAsync(attrs);
        toast.success('Supplier added');
      }
      onClose();
    } catch {
      toast.error('Could not save the supplier.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit supplier' : 'Add supplier'}
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add supplier'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Wella UK"
        />
      </Field>

      <div className="stock-modal-grid">
        <Field label="Contact email">
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Contact phone">
          <TextInput
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900123"
          />
        </Field>
      </div>

      <Field label="Account reference" hint="Your account number with this supplier (optional).">
        <TextInput value={accountRef} onChange={(e) => setAccountRef(e.target.value)} />
      </Field>

      <Field label="Notes">
        <TextInput value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {editing ? (
        <div className="stock-toggles" style={{ marginTop: 4 }}>
          <Toggle checked={active} onChange={setActive} label="Active" />
        </div>
      ) : null}
    </Modal>
  );
}
